import React, { useState } from 'react';
import { Layers, Zap, Package, Sparkles, Package2 } from 'lucide-react';
import { fabricCategories, fabricProducts, accessoryItems } from '../data/mockData';

const iconMap = {
  Layers: Layers,
  Zap: Zap,
  Package: Package,
  Sparkles: Sparkles,
  Package2: Package2
};

const ProductGrid = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleItems, setVisibleItems] = useState(8);

  const filteredAccessories = accessoryItems.filter(item =>
    item.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = fabricProducts.filter((product) => {
    const matchesCategory = selectedCategory === 'all' ? true : product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const displayedProducts = filteredProducts.slice(0, visibleItems);
  const hasMore = filteredProducts.length > visibleItems;

  const showAccessories = (selectedCategory === 'all' || selectedCategory === 'accessory') &&
    (filteredAccessories.length > 0);

  const handleLoadMore = () => {
    setVisibleItems(prev => prev + 8);
  };

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-primary">Fabric</span> Collection
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Premium quality fabrics for sportswear, garments, and fashion - Available in wholesale and retail
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search fabrics (e.g. Zurich, Superpoly, Cotton)..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setVisibleItems(8); // Reset pagination on search
              }}
              className="w-full px-6 py-3 rounded-full border-2 border-primary/20 focus:border-primary focus:outline-none shadow-sm transition-all"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Package size={20} className="text-primary/60" />
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => {
              setSelectedCategory('all');
              setVisibleItems(8);
            }}
            className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}>
            Complete Catalog
          </button>
          {fabricCategories.map((category) => {
            const IconComponent = iconMap[category.icon];
            return (
              <button
                key={category.id}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setVisibleItems(8);
                }}
                className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}>

                <IconComponent size={18} />
                {category.name}
              </button>);

          })}
        </div>

        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayedProducts.map((product, index) =>
            <div
              key={product.id}
              className="product-card group flex flex-col h-full"
              style={{ animationDelay: `${index * 0.05}s` }}>

              {product.image ?
                <div className="relative overflow-hidden rounded-lg mb-4 aspect-square">
                  <img
                    src={product.image}
                    alt={product.alt || product.name}
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {product.gsm &&
                    <div className="absolute top-3 right-3">
                      <span className="badge-accent">{product.gsm}</span>
                    </div>
                  }
                </div> :

                <div className="relative overflow-hidden rounded-lg mb-4 aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <Package size={64} className="text-gray-400" />
                </div>
              }

              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <p className="text-gray-600 text-sm mb-4 flex-grow">{product.description}</p>

              <a
                href={`https://wa.me/919713889720?text=${encodeURIComponent(`Hello, I'm interested in ${product.name}. Please share details.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-sm self-start">
                Inquire Now
              </a>
            </div>
          )}
        </div>

        {/* Load More Button - Best UI Standard */}
        {hasMore && (
          <div className="text-center mt-12">
            <button
              onClick={handleLoadMore}
              className="btn-secondary px-10 py-3 rounded-full hover:scale-105 transition-transform font-bold"
            >
              Load More Products ({filteredProducts.length - visibleItems} remaining)
            </button>
          </div>
        )}

        {/* Empty State */}
        {filteredProducts.length === 0 && !showAccessories && (
          <div className="text-center py-20">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600">
              {selectedCategory === 'accessory' && searchTerm
                ? `No accessories matching "${searchTerm}"`
                : `No fabrics found matching "${searchTerm}"`}
            </h3>
            <button onClick={() => { setSearchTerm(''); setVisibleItems(8); }} className="text-primary mt-2 font-semibold">Clear Search</button>
          </div>
        )}

        {/* Accessories Section */}
        {showAccessories &&
          <div className="mt-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <Package2 className="text-primary" size={32} />
                <h3 className="!font-bold !text-2xl text-gray-900">Accessory Materials</h3>
              </div>
              <p className="text-gray-600 mb-6">
                We also offer a wide range of accessory materials and supplies for garment retailers:
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAccessories.map((item, index) =>
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                )}
              </div>
              <div className="mt-6 p-4 bg-teal-50 rounded-lg border-l-4 border-primary">
                <p className="text-gray-700">
                  <span className="font-semibold text-primary">Contact us</span> for pricing and availability of accessory materials.
                </p>
              </div>
            </div>
          </div>
        }

        {/* View All CTA */}
        <div className="text-center mt-10">
          <a
            href={`https://wa.me/919713889720?text=${encodeURIComponent('Hello, I would like to see your complete fabric catalog')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary btn-lg inline-flex items-center gap-2">

            View Complete Catalog
          </a>
        </div>
      </div>
    </section>);

};

export default ProductGrid;